const clampInteger = ( value, minimum, maximum, fallback ) => {
	const parsed = Number.parseInt( value, 10 );
	return Number.isFinite( parsed )
		? Math.min( maximum, Math.max( minimum, parsed ) )
		: fallback;
};

export function normalizeBreakpoint( breakpoint ) {
	if ( ! breakpoint || typeof breakpoint !== 'object' ) {
		return null;
	}

	const width = Number.parseInt( breakpoint.width, 10 );
	if ( ! Number.isFinite( width ) || width < 1 ) {
		return null;
	}

	return {
		width,
		slidesPerView: clampInteger( breakpoint.slidesPerView, 1, 10, 1 ),
		slidesPerGroup: clampInteger( breakpoint.slidesPerGroup, 1, 10, 1 ),
	};
}

export function sortBreakpoints( breakpoints = [] ) {
	return breakpoints
		.map( normalizeBreakpoint )
		.filter( Boolean )
		.sort( ( first, second ) => first.width - second.width );
}

export function resolveResponsiveSettings(
	baseSettings,
	breakpoints,
	viewportWidth
) {
	const base = {
		slidesPerView: clampInteger( baseSettings.slidesPerView, 1, 10, 1 ),
		slidesPerGroup: clampInteger( baseSettings.slidesPerGroup, 1, 10, 1 ),
	};
	const match = sortBreakpoints( breakpoints ).find(
		( breakpoint ) => viewportWidth <= breakpoint.width
	);
	return match
		? {
				slidesPerView: match.slidesPerView,
				slidesPerGroup: match.slidesPerGroup,
		  }
		: base;
}

/**
 * Cap slides-per-view so no slide renders narrower than a minimum width.
 *
 * The configured count (base or breakpoint) stays the ceiling — this only
 * ever reduces it, fitting as many minimum-width slides as the carousel is
 * currently wide (never fewer than one). Groups shrink with the view so a
 * scroll never advances past what is visible.
 *
 * @param {Object} settings      { slidesPerView, slidesPerGroup }.
 * @param {number} carouselWidth Current carousel width in px.
 * @param {number} gap           Gap between slides in px.
 * @param {number} minSlideWidth Minimum slide width in px (0/absent = off).
 * @return {Object} Possibly reduced settings.
 */
export function capSlidesToMinWidth(
	settings,
	carouselWidth,
	gap,
	minSlideWidth
) {
	const minimum = Number.parseFloat( minSlideWidth );
	const width = Number.parseFloat( carouselWidth );
	if (
		! Number.isFinite( minimum ) ||
		minimum <= 0 ||
		! Number.isFinite( width ) ||
		width <= 0
	) {
		return settings;
	}
	const spacing = Number.isFinite( Number.parseFloat( gap ) )
		? Math.max( 0, Number.parseFloat( gap ) )
		: 0;
	const fit = Math.max(
		1,
		Math.floor( ( width + spacing ) / ( minimum + spacing ) )
	);
	const slidesPerView = Math.min( settings.slidesPerView, fit );
	return {
		slidesPerView,
		slidesPerGroup: Math.min( settings.slidesPerGroup, slidesPerView ),
	};
}
